import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { FeedEvent, FeedSocketClient } from '../../socket';
import {
  QK_DASHBOARD_CONSENT_STATUS,
  QK_DASHBOARD_ORG_STATUS,
  QK_PATIENT_LIST,
  QK_SURVEY_APPOINTMENT_LIST,
  QK_SURVEY_SESSION_LIST,
} from '../query-keys';

/**
 * feed.events 실시간 신호 → React Query invalidate.
 *
 * 이벤트는 "무엇이 바뀌었다"는 신호일 뿐 — 데이터는 안 싣는다. 해당 쿼리를 무효화해 REST 재조회(데이터 정본=REST).
 * 소켓 연결/생명주기는 FeedSocketProvider 가 소유하고, 이 훅은 넘겨받은 소켓에 invalidate 구독만 붙인다
 * (같은 소켓을 PatientProvider 도 구독해 스토어를 갱신 — 단일 연결 공유).
 */
export const useFeedInvalidation = (socket?: FeedSocketClient) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!socket) return;

    const invalidate = (event: FeedEvent) => {
      // 통합 대시보드(org 현황·오늘의 동의현황)는 어떤 신호든 갱신.
      queryClient.invalidateQueries({ queryKey: [QK_DASHBOARD_ORG_STATUS] });
      queryClient.invalidateQueries({ queryKey: [QK_DASHBOARD_CONSENT_STATUS] });
      if (event.service === 'survey') {
        queryClient.invalidateQueries({ queryKey: [QK_SURVEY_SESSION_LIST, event.patientId] });
        // 예약(Appointment)도 survey 서비스 이벤트(APPOINTMENT_*)로 발행되며, 설문 이벤트(세션 생성이
        // 예약을 소비하는 흐름) 수신 시에도 함께 갱신해 다른 PC의 유령 검사예정(→중복 발송)을 줄인다.
        queryClient.invalidateQueries({ queryKey: [QK_SURVEY_APPOINTMENT_LIST] });
      } else if (event.service === 'patient') {
        queryClient.invalidateQueries({ queryKey: [QK_PATIENT_LIST] });
      }
    };

    const subId = socket.subscribe(
      [
        'USER_AGREED',
        'AGREEMENT_SENT',
        'PATIENT_CREATED',
        'PATIENT_UPDATED',
        'PATIENT_DELETED',
        'SURVEY_SESSION_CREATED',
        'SURVEY_SESSION_UPDATED',
        'SURVEY_SESSION_DELETED',
        'APPOINTMENT_CREATED',
        'APPOINTMENT_UPDATED',
        'APPOINTMENT_DELETED',
      ],
      invalidate,
    );

    // 재연결 시 끊긴 동안 놓친 이벤트 보정 — 관련 쿼리 전체 무효화.
    socket.onConnect(() => {
      queryClient.invalidateQueries({ queryKey: [QK_DASHBOARD_ORG_STATUS] });
      queryClient.invalidateQueries({ queryKey: [QK_DASHBOARD_CONSENT_STATUS] });
      queryClient.invalidateQueries({ queryKey: [QK_SURVEY_SESSION_LIST] });
      queryClient.invalidateQueries({ queryKey: [QK_SURVEY_APPOINTMENT_LIST] });
      queryClient.invalidateQueries({ queryKey: [QK_PATIENT_LIST] });
    });

    return () => {
      socket.unsubscribe(subId);
    };
  }, [socket, queryClient]);
};
